import {
  type CollisionDetection,
  DndContext,
  type DragEndEvent,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Box, Center } from "@mantine/core";
import {
  type ColumnDef,
  type OnChangeFn,
  type PaginationState,
  type RowData,
  type RowSelectionState,
  type SortingState,
} from "@tanstack/react-table-v9";
import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { spacing } from "#app/theme/v2/spacing";

import { Checkbox } from "../../atoms/Checkbox/Checkbox";
import { Text } from "../../atoms/Typographies/Text";
import classes from "./Table.module.css";
import { TableHeader } from "./TableHeader";
import { type RowAction, type TableAppFeatures, useAppTable } from "./tableHook";
import { TablePagination } from "./TablePagination";
import { TABLE_DND_COLUMN_WIDTH, TABLE_SELECTION_COLUMN_WIDTH, TableRow } from "./TableRow";

const EMPTY_INDETERMINATE_ROW_IDS: ReadonlySet<string> = new Set();

// Rows are memoized, so a parent would keep a stale partial state when only one of its children
// toggles. A context lets each selection checkbox re-render on its own when the set changes.
const IndeterminateRowIdsContext = createContext<ReadonlySet<string>>(EMPTY_INDETERMINATE_ROW_IDS);

type RowSelectionCheckboxProps = {
  rowId: string;
  checked: boolean;
  someSelected: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const RowSelectionCheckbox = ({
  rowId,
  checked,
  someSelected,
  onChange,
}: RowSelectionCheckboxProps) => {
  const { t } = useTranslation("components");
  const indeterminateRowIds = useContext(IndeterminateRowIdsContext);

  return (
    <Checkbox
      checked={checked}
      indeterminate={!checked && (indeterminateRowIds.has(rowId) || someSelected)}
      onChange={onChange}
      aria-label={t("table.selectRow")}
    />
  );
};

type TableProps<TData extends RowData> = {
  data: TData[];
  // `any` is intentional: each column has a different TValue (string, number, …) and
  // strictFunctionTypes makes ColumnDef<…, string> not assignable to ColumnDef<…, unknown>
  // due to contravariance in cell/header/footer params. Table never reads TValue directly.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TableAppFeatures, TData, any>[];
  defaultSorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  enableSorting?: boolean;
  defaultRowSelection?: RowSelectionState;
  rowSelection?: RowSelectionState;
  indeterminateRowIds?: ReadonlySet<string>;
  onRowSelectionChange?: (selectedIds: string[]) => void;
  enableRowSelection?: boolean | ((row: TData) => boolean);
  enableDnd?: boolean;
  isDndColumnInGutter?: boolean;
  onRowOrderChange?: (newData: TData[], movedRow: TData) => void;
  getRowDragGroupKey?: (row: TData) => string | undefined;
  canDragRow?: (row: TData) => boolean;
  onDragStart?: (row: TData) => void;
  onDragSettled?: () => void;
  dragHandleLabel?: React.ReactNode;
  enablePagination?: boolean;
  pagination?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;
  pageCount?: number;
  loading?: boolean;
  loadingState?: React.ReactNode;
  emptyState?: React.ReactNode;
  getRowId?: (row: TData) => string;
  rowActions?: RowAction<TData>[];
  rowActionsCellOverlay?: string;
  onRowClick?: (row: TData) => void;
  getRowHeight?: (row: TData) => string;
  getRowError?: (row: TData) => boolean;
  getCellError?: (row: TData, columnId: string) => boolean;
  isRowDividerVisible?: (row: TData) => boolean;
  getRowGroupKey?: (row: TData) => string | undefined;
  getRowExtraTopPadding?: (row: TData) => string | undefined;
  getRowExtraBottomPadding?: (row: TData) => string | undefined;
};

export type TableHandle = {
  resetRowSelection: () => void;
};

// Rows can only be reordered against other rows sharing the same getRowDragGroupKey (e.g. an
// assembly's children stay scoped to that assembly). Reordering happens by moving each row only
// within its own group's slots in `data`, so rows from other groups interleaved in between never
// get displaced.
const reorderWithinGroup = <TData,>(
  data: TData[],
  activeId: string,
  overId: string,
  resolveRowId: (row: TData, index: number) => string,
  getGroupKey?: (row: TData) => string | undefined,
): TData[] | null => {
  const entries = data.map((row, index) => ({
    row,
    id: resolveRowId(row, index),
    index,
  }));
  const activeEntry = entries.find((entry) => entry.id === activeId);
  const overEntry = entries.find((entry) => entry.id === overId);
  if (!activeEntry || !overEntry) {
    return null;
  }

  const activeGroup = getGroupKey?.(activeEntry.row);
  const overGroup = getGroupKey?.(overEntry.row);
  if (activeGroup !== overGroup) {
    return null;
  }

  const groupEntries = entries.filter((entry) => getGroupKey?.(entry.row) === activeGroup);
  const oldIndexInGroup = groupEntries.findIndex((entry) => entry.id === activeId);
  const newIndexInGroup = groupEntries.findIndex((entry) => entry.id === overId);
  const reorderedGroupRows = arrayMove(
    groupEntries.map((entry) => entry.row),
    oldIndexInGroup,
    newIndexInGroup,
  );

  const result = [...data];
  groupEntries.forEach((entry, position) => {
    result[entry.index] = reorderedGroupRows[position]!;
  });
  return result;
};

const TableInner = <TData extends RowData>(
  {
    data,
    columns,
    defaultSorting = [],
    onSortingChange,
    enableSorting = true,
    defaultRowSelection = {},
    rowSelection: controlledRowSelection,
    indeterminateRowIds = EMPTY_INDETERMINATE_ROW_IDS,
    onRowSelectionChange,
    enableRowSelection = true,
    enableDnd = false,
    isDndColumnInGutter = false,
    onRowOrderChange,
    getRowDragGroupKey,
    canDragRow,
    onDragStart,
    onDragSettled,
    dragHandleLabel,
    enablePagination = false,
    pagination: controlledPagination,
    onPaginationChange,
    pageCount,
    loading = false,
    loadingState,
    emptyState,
    getRowId,
    rowActions,
    rowActionsCellOverlay,
    onRowClick,
    getRowHeight,
    getRowError,
    getCellError,
    isRowDividerVisible,
    getRowGroupKey,
    getRowExtraTopPadding,
    getRowExtraBottomPadding,
  }: TableProps<TData>,
  ref: React.ForwardedRef<TableHandle>,
) => {
  const { t } = useTranslation("components");

  const firstColumnIsSticky = columns[0]?.meta?.sticky === true;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allColumns: ColumnDef<TableAppFeatures, TData, any>[] = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const selectionColumn: ColumnDef<TableAppFeatures, TData, any> = {
      id: "__select__",
      size: TABLE_SELECTION_COLUMN_WIDTH,
      enableSorting: false,
      meta: firstColumnIsSticky ? { sticky: true } : undefined,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          indeterminate={table.getIsSomePageRowsSelected()}
          onChange={
            table.getToggleAllPageRowsSelectedHandler() as (
              e: React.ChangeEvent<HTMLInputElement>,
            ) => void
          }
          aria-label={t("table.selectAll")}
        />
      ),
      cell: ({ row }) =>
        row.getCanSelect() ? (
          <RowSelectionCheckbox
            rowId={row.id}
            checked={row.getIsSelected()}
            someSelected={row.getIsSomeSelected()}
            onChange={
              row.getToggleSelectedHandler() as (e: React.ChangeEvent<HTMLInputElement>) => void
            }
          />
        ) : null,
    };

    return [...(enableRowSelection ? [selectionColumn] : []), ...columns];
  }, [columns, enableRowSelection, firstColumnIsSticky, t]);

  const stickyOffsets: Record<string, number> = {};
  let lastStickyColumnId: string | undefined;
  let stickyCursor = enableDnd ? TABLE_DND_COLUMN_WIDTH : 0;
  allColumns.forEach((column) => {
    if (!column.meta?.sticky || column.id === undefined) {
      return;
    }
    stickyOffsets[column.id] = stickyCursor;
    lastStickyColumnId = column.id;
    stickyCursor += typeof column.size === "number" ? column.size : 0;
  });

  const resolveRowId = (row: TData, index: number) => (getRowId ? getRowId(row) : String(index));

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !lastStickyColumnId) {
      return;
    }

    const handleScroll = () => {
      wrapper.classList.toggle(classes.scrolled!, wrapper.scrollLeft > 0);
    };

    handleScroll();
    wrapper.addEventListener("scroll", handleScroll, { passive: true });
    return () => wrapper.removeEventListener("scroll", handleScroll);
  }, [lastStickyColumnId]);

  // In v9, onSortingChange/onRowSelectionChange are the atom writers (via makeStateUpdater default).
  // Passing undefined overrides the default and breaks internal state. Use effects for notification instead.
  const onSortingChangeRef = useRef(onSortingChange);
  onSortingChangeRef.current = onSortingChange;
  const onRowSelectionChangeRef = useRef(onRowSelectionChange);
  onRowSelectionChangeRef.current = onRowSelectionChange;
  const onPaginationChangeRef = useRef(onPaginationChange);
  onPaginationChangeRef.current = onPaginationChange;
  const controlledRowSelectionRef = useRef(controlledRowSelection);
  controlledRowSelectionRef.current = controlledRowSelection;

  const isRowSelectionControlled = controlledRowSelection !== undefined;

  const table = useAppTable<TData>({
    data,
    columns: allColumns,
    defaultColumn: { size: undefined, minSize: undefined, maxSize: undefined },
    initialState: {
      sorting: defaultSorting,
      rowSelection: defaultRowSelection,
    },
    state: {
      pagination: controlledPagination,
      ...(isRowSelectionControlled ? { rowSelection: controlledRowSelection } : {}),
    },
    ...(onPaginationChange ? { onPaginationChange } : {}),
    ...(isRowSelectionControlled
      ? {
          onRowSelectionChange: (updater) => {
            const current = controlledRowSelectionRef.current ?? {};
            const next = typeof updater === "function" ? updater(current) : updater;
            onRowSelectionChangeRef.current?.(Object.keys(next).filter((id) => next[id]));
          },
        }
      : {}),
    enableRowSelection:
      typeof enableRowSelection === "function"
        ? (row) => enableRowSelection(row.original)
        : enableRowSelection,
    enableSorting,
    manualPagination: true,
    ...(pageCount !== undefined ? { pageCount } : {}),
    ...(getRowId ? { getRowId: (row) => getRowId(row) } : {}),
    meta: { rowActions, stickyOffsets, lastStickyColumnId },
  });

  useImperativeHandle(
    ref,
    () => ({
      resetRowSelection: () => table.setRowSelection({}),
    }),
    [table],
  );

  const { sorting, rowSelection } = table.state;
  const prevSorting = useRef(sorting);
  const prevRowSelection = useRef(rowSelection);

  useEffect(() => {
    if (prevSorting.current !== sorting) {
      prevSorting.current = sorting;
      onSortingChangeRef.current?.(sorting);
    }
  }, [sorting]);

  useEffect(() => {
    if (isRowSelectionControlled) {
      return;
    }
    if (prevRowSelection.current !== rowSelection) {
      prevRowSelection.current = rowSelection;
      onRowSelectionChangeRef.current?.(Object.keys(rowSelection).filter((id) => rowSelection[id]));
    }
  }, [isRowSelectionControlled, rowSelection]);

  const rows = table.getRowModel().rows;
  const isEmptyState = !loading && rows.length === 0;
  const headerGroups = table.getHeaderGroups();
  const isDndEnabled = enableDnd && table.state.sorting.length === 0;
  const isRowDraggable = (row: TData) => isDndEnabled && (canDragRow ? canDragRow(row) : true);

  const [draggingRowId, setDraggingRowId] = useState<string | null>(null);
  const activeGroupRef = useRef<string | undefined>(undefined);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    const index = data.findIndex((row, i) => resolveRowId(row, i) === event.active.id);
    const activeRow = index !== -1 ? data[index] : undefined;
    activeGroupRef.current = activeRow ? getRowDragGroupKey?.(activeRow) : undefined;
    setDraggingRowId(String(event.active.id));
    if (activeRow) {
      onDragStart?.(activeRow);
    }
  };

  const handleDragEndOrCancel = () => {
    setDraggingRowId(null);
    activeGroupRef.current = undefined;
    onDragSettled?.();
  };

  const collisionDetectionWithGroup: CollisionDetection = (args) => {
    if (!getRowDragGroupKey) {
      return closestCenter(args);
    }
    const filteredContainers = args.droppableContainers.filter((container) => {
      const index = data.findIndex((row, i) => resolveRowId(row, i) === container.id);
      const candidateRow = index !== -1 ? data[index] : undefined;
      return candidateRow ? getRowDragGroupKey(candidateRow) === activeGroupRef.current : false;
    });
    return closestCenter({ ...args, droppableContainers: filteredContainers });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    handleDragEndOrCancel();
    if (!over || active.id === over.id) {
      return;
    }

    const reordered = reorderWithinGroup(
      data,
      String(active.id),
      String(over.id),
      resolveRowId,
      getRowDragGroupKey,
    );

    if (reordered) {
      const activeIndex = data.findIndex((row, i) => resolveRowId(row, i) === String(active.id));
      const activeRow = activeIndex !== -1 ? data[activeIndex] : undefined;
      if (activeRow) {
        onRowOrderChange?.(reordered, activeRow);
      }
    }
  };

  const rowIds = rows.map((r) => r.id);

  const groupConsecutiveRowsByKey = (tableRows: typeof rows) => {
    const groupedRows: { key: string | undefined; rows: typeof rows }[] = [];

    tableRows.forEach((row) => {
      const key = getRowGroupKey?.(row.original);
      const lastGroup = groupedRows.at(-1);
      if (key !== undefined && lastGroup?.key === key) {
        lastGroup.rows.push(row);
      } else {
        groupedRows.push({ key, rows: [row] });
      }
    });

    return groupedRows;
  };

  const rowRuns = groupConsecutiveRowsByKey(rows);

  let renderedRowCount = 0;
  const groupedRowBodies = rowRuns.map((run) => (
    <Box
      key={run.rows[0]!.id}
      component="tbody"
      className={run.key !== undefined ? classes.rowGroup : undefined}
    >
      {run.rows.map((row, idxInRun) => {
        const currentIndex = renderedRowCount++;
        return (
          <TableRow
            key={row.id}
            row={row}
            isSelected={row.getIsSelected()}
            rowActions={rowActions}
            actionsCellOverlay={rowActionsCellOverlay}
            stickyOffsets={stickyOffsets}
            lastStickyColumnId={lastStickyColumnId}
            enableDnd={enableDnd}
            isDraggable={isRowDraggable(row.original)}
            dragHandleLabel={dragHandleLabel}
            isLast={currentIndex === rows.length - 1}
            onRowClick={onRowClick}
            height={getRowHeight?.(row.original)}
            isError={getRowError?.(row.original)}
            getCellError={getCellError}
            showDivider={isRowDividerVisible?.(row.original)}
            renderActionsCell={idxInRun === 0}
            actionsRowSpan={run.rows.length}
            extraTopPadding={getRowExtraTopPadding?.(row.original)}
            extraBottomPadding={getRowExtraBottomPadding?.(row.original)}
          />
        );
      })}
    </Box>
  ));

  const tableContent = (
    <>
      <Box component="table" className={classes.table}>
        <Box component="thead">
          <TableHeader
            headerGroups={headerGroups}
            enableRowSelection={!!enableRowSelection}
            enableDnd={enableDnd}
          />
        </Box>
        {loading && rows.length === 0 ? (
          <Box component="tbody">
            {loadingState ?? (
              <Box component="tr">
                <Box
                  component="td"
                  colSpan={allColumns.length + (rowActions?.length ? 1 : 0)}
                  style={{
                    padding: `${spacing[6]} ${spacing[3]}`,
                    textAlign: "center",
                  }}
                >
                  <Text size="small" type="regular" color={colors.text.secondaryDefault}>
                    {t("table.loading")}
                  </Text>
                </Box>
              </Box>
            )}
          </Box>
        ) : isEmptyState ? null : (
          groupedRowBodies
        )}
      </Box>
      {isEmptyState && (
        <Center style={{ flex: 1, minHeight: 0, width: "100%" }}>
          {emptyState ?? (
            <Text size="small" type="regular" color={colors.text.secondaryDefault}>
              {t("table.empty")}
            </Text>
          )}
        </Center>
      )}
    </>
  );

  return (
    <Box
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        ...(isDndColumnInGutter
          ? {
              marginLeft: -TABLE_DND_COLUMN_WIDTH,
              paddingLeft: enableDnd ? undefined : TABLE_DND_COLUMN_WIDTH,
            }
          : {}),
      }}
    >
      <IndeterminateRowIdsContext.Provider value={indeterminateRowIds}>
        <table.AppTable>
          <Box
            ref={wrapperRef}
            style={
              {
                display: isEmptyState ? "flex" : undefined,
                flexDirection: isEmptyState ? "column" : undefined,
                overflow: isEmptyState ? "hidden" : undefined,
                "--table-cell-bg": colors.bg.elevation.primary,
                "--table-cell-hover-bg": colors.bg.elevation.secondary,
                "--table-cell-selected-bg": colors.bg.elevation.tertiary,
                "--table-row-error-bg": colors.bg.status.error.primary,
                "--table-row-error-border": colors.border.error.secondary,
                "--table-header-border": colors.border.quaternary,
                "--table-icon-color": colors.icon.tertiary,
                "--table-icon-active-color": colors.text.primaryDefault,
                "--table-icon-drag-hover-color": colors.icon.secondary,
                "--table-gap": spacing[1],
                cursor: draggingRowId ? "grabbing" : undefined,
              } as React.CSSProperties
            }
            className={classes.tableWrapper}
          >
            {enableDnd ? (
              <DndContext
                sensors={sensors}
                collisionDetection={collisionDetectionWithGroup}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragEndOrCancel}
              >
                <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
                  {tableContent}
                </SortableContext>
              </DndContext>
            ) : (
              tableContent
            )}
          </Box>
          {enablePagination ? <TablePagination /> : null}
        </table.AppTable>
      </IndeterminateRowIdsContext.Provider>
    </Box>
  );
};

const Table = forwardRef(TableInner) as <TData extends RowData>(
  props: TableProps<TData> & { ref?: React.ForwardedRef<TableHandle> },
) => ReturnType<typeof TableInner>;

export { Table };
